import React, { useState, useEffect } from 'react';
import { 
  Package, 
  Save, 
  Edit, 
  Plus, 
  Trash2, 
  RefreshCw,
  AlertCircle,
  CheckCircle,
  X,
  Settings
} from 'lucide-react';
import { loadSubscriptionPlans, saveSubscriptionPlan } from '../utils/subscriptionUtils';
import { syncAllCatalogPlansToApp } from '../utils/planCatalogSync';
import { VALID_PLAN_IDS } from '../constants/subscriptionContract';
import { backofficeAuth } from '../backofficeFirebaseClient';

const PlanCatalogManager = () => {
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editingPlan, setEditingPlan] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    price: 0,
    currency: '₹',
    billing: 'monthly',
    limits: {
      firebase_operations: 0,
      storage_mb: 0,
      sales_documents: 0,
      parties: 0
    },
    description: ''
  });

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const firebasePlans = await loadSubscriptionPlans();
      setPlans(firebasePlans || {});
      console.log('✅ Plans loaded:', Object.keys(firebasePlans || {}));
    } catch (error) {
      console.error('❌ Error loading plans:', error);
      setMessage({ type: 'error', text: `Error loading plans: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (planId) => {
    const plan = plans[planId];
    if (plan) {
      setFormData({
        id: plan.id || planId,
        name: plan.name || '',
        price: plan.price || 0,
        currency: plan.currency || '₹',
        billing: plan.billing || 'monthly',
        limits: {
          firebase_operations: plan.limits?.firebase_operations || 0,
          storage_mb: plan.limits?.storage_mb || 0,
          sales_documents: plan.limits?.sales_documents || 0,
          parties: plan.limits?.parties || 0
        },
        description: plan.description || ''
      });
      setEditingPlan(planId);
      setShowAddModal(true);
    }
  };

  const handleAddNew = () => {
    setFormData({
      id: '',
      name: '',
      price: 0,
      currency: '₹',
      billing: 'monthly',
      limits: {
        firebase_operations: 0,
        storage_mb: 0,
        sales_documents: 0,
        parties: 0
      },
      description: ''
    });
    setEditingPlan(null);
    setShowAddModal(true);
  };

  const handleSyncAllToApp = async () => {
    try {
      setLoading(true);
      const synced = await syncAllCatalogPlansToApp(plans);
      setMessage({
        type: 'success',
        text: `Synced ${synced} plan(s) to app Firestore (plans/).`
      });
    } catch (error) {
      console.error('❌ Error syncing plans to app:', error);
      setMessage({ type: 'error', text: `Sync failed: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.id || !formData.id.trim()) {
        setMessage({ type: 'error', text: 'Plan ID is required' });
        return;
      }
      if (!formData.name || !formData.name.trim()) {
        setMessage({ type: 'error', text: 'Plan name is required' });
        return;
      }
      if (formData.price < 0) {
        setMessage({ type: 'error', text: 'Price must be 0 or greater' });
        return;
      }
      const limitKeys = ['firebase_operations', 'storage_mb', 'sales_documents', 'parties'];
      if (limitKeys.some((k) => Number(formData.limits[k] ?? 0) < 0)) {
        setMessage({ type: 'error', text: 'Limits must be 0 or greater' });
        return;
      }

      setLoading(true);
      
      const user = backofficeAuth.currentUser;
      if (!user) {
        throw new Error('Admin authentication required');
      }

      const adminInfo = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || user.email
      };

      // Normalize plan ID (remove spaces, convert to lowercase)
      const planId = formData.id.trim().toLowerCase().replace(/\s+/g, '-');

      if (!VALID_PLAN_IDS.includes(planId)) {
        setMessage({
          type: 'error',
          text: `Plan ID must be one of: ${VALID_PLAN_IDS.join(', ')}`
        });
        return;
      }
      
      // Prepare plan data in USAGE_PLANS format (compatible with SubscriptionManager)
      const planData = {
        id: planId,
        name: formData.name.trim(),
        price: Number(formData.price),
        currency: formData.currency,
        billing: formData.billing,
        limits: {
          firebase_operations: Number(formData.limits.firebase_operations),
          storage_mb: Number(formData.limits.storage_mb),
          sales_documents: Number(formData.limits.sales_documents || 0),
          parties: Number(formData.limits.parties || 0)
        },
        description: formData.description.trim()
      };

      await saveSubscriptionPlan(planId, planData, adminInfo);
      
      setMessage({ 
        type: 'success', 
        text: `Plan "${planData.name}" ${editingPlan ? 'updated' : 'created'} and synced to app plans/ collection.` 
      });
      
      setShowAddModal(false);
      setEditingPlan(null);
      
      // Reload plans
      await loadPlans();
      
    } catch (error) {
      console.error('❌ Error saving plan:', error);
      setMessage({ type: 'error', text: `Error saving plan: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowAddModal(false);
    setEditingPlan(null);
    setFormData({
      id: '',
      name: '',
      price: 0,
      currency: '₹',
      billing: 'monthly',
      limits: {
        firebase_operations: 0,
        storage_mb: 0,
        sales_documents: 0,
        parties: 0
      },
      description: ''
    });
  };

  const formatNumber = (num) => {
    return Number(num).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8 text-blue-600" />
              <h1 className="text-3xl font-bold text-gray-800">Plan Catalog Manager</h1>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleSyncAllToApp}
                disabled={loading || Object.keys(plans).length === 0}
                className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 disabled:opacity-50 flex items-center gap-2"
                title="Push all catalog plans to acc-app plans/ for frontend limits"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Sync all to app
              </button>
              <button
                onClick={loadPlans}
                disabled={loading}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 disabled:opacity-50 flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={handleAddNew}
                disabled={loading}
                className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add New Plan
              </button>
            </div>
          </div>
          <p className="text-gray-600">
            Manage subscription plans in backoffice catalog; saves mirror to app <code className="text-xs bg-gray-100 px-1 rounded">plans/</code> for frontend limits.
          </p>
        </div>

        {/* Message Display */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {message.type === 'success' ? 
              <CheckCircle className="w-5 h-5" /> : 
              <AlertCircle className="w-5 h-5" />
            }
            <span>{message.text}</span>
            <button
              onClick={() => setMessage({ type: '', text: '' })}
              className="ml-auto text-gray-500 hover:text-gray-700"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Plans Grid */}
        {loading && Object.keys(plans).length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <RefreshCw className="w-12 h-12 text-gray-400 mx-auto mb-4 animate-spin" />
            <p className="text-gray-600">Loading plans...</p>
          </div>
        ) : Object.keys(plans).length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">No Plans Found</h3>
            <p className="text-gray-600 mb-4">Get started by adding your first subscription plan</p>
            <button
              onClick={handleAddNew}
              className="px-6 py-3 bg-green-500 text-white rounded-md hover:bg-green-600 flex items-center gap-2 mx-auto"
            >
              <Plus className="w-5 h-5" />
              Add New Plan
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(plans).map(([planId, plan]) => (
              <div
                key={planId}
                className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200 hover:border-blue-300 transition-all"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-800">{plan.name || planId}</h3>
                  <button
                    onClick={() => handleEdit(planId)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                    title="Edit plan"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="mb-4">
                  <div className="text-3xl font-bold text-blue-600 mb-1">
                    {plan.currency}{formatNumber(plan.price)}
                  </div>
                  <div className="text-sm text-gray-600">/{plan.billing || 'monthly'}</div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Operations:</span>
                    <span className="font-semibold">{formatNumber(plan.limits?.firebase_operations || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Storage:</span>
                    <span className="font-semibold">{formatNumber(plan.limits?.storage_mb || 0)} MB</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Sales docs:</span>
                    <span className="font-semibold">{formatNumber(plan.limits?.sales_documents || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Parties:</span>
                    <span className="font-semibold">{formatNumber(plan.limits?.parties || 0)}</span>
                  </div>
                </div>

                {plan.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{plan.description}</p>
                )}

                <div className="pt-4 border-t border-gray-200">
                  <div className="text-xs text-gray-500">
                    Plan ID: <span className="font-mono">{planId}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <Settings className="w-6 h-6 text-blue-600" />
                  {editingPlan ? 'Edit Plan' : 'Add New Plan'}
                </h2>
                <button
                  onClick={handleCancel}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Plan ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Plan ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.id}
                    onChange={(e) => handleInputChange('id', e.target.value)}
                    disabled={!!editingPlan}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="e.g., starter, basic, pro"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {editingPlan ? 'Plan ID cannot be changed' : 'Lowercase, no spaces (will be auto-formatted)'}
                  </p>
                </div>

                {/* Plan Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Plan Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Starter Plan"
                  />
                </div>

                {/* Price and Currency */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Price <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) => handleInputChange('price', e.target.value)}
                      min="0"
                      step="0.01"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Currency
                    </label>
                    <input
                      type="text"
                      value={formData.currency}
                      onChange={(e) => handleInputChange('currency', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="₹"
                    />
                  </div>
                </div>

                {/* Billing Period */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Billing Period
                  </label>
                  <select
                    value={formData.billing}
                    onChange={(e) => handleInputChange('billing', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                    <option value="lifetime">Lifetime</option>
                  </select>
                </div>

                {/* Limits */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Limits</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Firebase Operations
                      </label>
                      <input
                        type="number"
                        value={formData.limits.firebase_operations}
                        onChange={(e) => handleInputChange('limits.firebase_operations', e.target.value)}
                        min="0"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Storage (MB)
                      </label>
                      <input
                        type="number"
                        value={formData.limits.storage_mb}
                        onChange={(e) => handleInputChange('limits.storage_mb', e.target.value)}
                        min="0"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sales documents
                      </label>
                      <input
                        type="number"
                        value={formData.limits.sales_documents}
                        onChange={(e) => handleInputChange('limits.sales_documents', e.target.value)}
                        min="0"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Parties
                      </label>
                      <input
                        type="number"
                        value={formData.limits.parties}
                        onChange={(e) => handleInputChange('limits.parties', e.target.value)}
                        min="0"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Plan description..."
                  />
                </div>
              </div>

              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex items-center justify-end gap-3">
                <button
                  onClick={handleCancel}
                  disabled={loading}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {editingPlan ? 'Update Plan' : 'Create Plan'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanCatalogManager;




